// @ts-nocheck
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ReactMuiTableColumnHeaderTextEllipsis,
  ReactMuiTableVirtualizedV8,
} from '@nuva-org/solytics-frontend';
import PropTypes from 'prop-types';
import { ReactComponent as CloseIcon } from '../../../assets/Icons/lucideIcons/x.svg';

import { createDynamicColumns } from '../../../utils/Utils';
import NoDataPresentComponent from '../NoDataPresentComponent';
import SearchTextField from '../SearchTextField';
import DialogTransition from '../Transition/DialogTransition';

const getChartSource = (chartValue) => {
  if (typeof chartValue !== 'string') {
    return '';
  }

  const trimmedChartValue = chartValue.trim();
  if (!trimmedChartValue || trimmedChartValue.startsWith('data:image/')) {
    return trimmedChartValue;
  }

  return `data:image/png;base64,${trimmedChartValue}`;
};

const filterTableData = (tableData, searchText) => {
  const normalizedSearchText = searchText?.trim()?.toLowerCase();

  if (!normalizedSearchText) {
    return tableData;
  }

  return tableData.filter((row) =>
    Object.values(row || {}).some((cellValue) =>
      String(cellValue ?? '')
        .toLowerCase()
        .includes(normalizedSearchText)
    )
  );
};

const PreviewTableChart = ({ open, handleClose, previewItem }) => {
  const [searchText, setSearchText] = useState('');

  const isTablePreview = previewItem?.type === 'table';
  const rawTableData = previewItem?.item?.data;
  const tableData = Array.isArray(rawTableData) ? rawTableData : [];

  const columns = useMemo(
    () =>
      createDynamicColumns({
        data: previewItem?.item?.columns || [],
        width: 150,
        HeaderCell: ReactMuiTableColumnHeaderTextEllipsis,
        showTimeWithSeconds: true,
      }),
    [previewItem?.item?.columns]
  );

  const filteredTableData = useMemo(
    () => filterTableData(tableData, searchText),
    [tableData, searchText]
  );

  const handleClosePreview = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const handleSearchChange = useCallback(
    (event) => {
      setSearchText(event?.target?.value || '');
    },
    [setSearchText]
  );

  const getPreviewCellProps = useCallback((cell) => {
    return {
      title: cell?.value ? String(cell.value) : '',
    };
  }, []);

  const getPreviewColumnProps = useCallback(() => {
    return {
      style: {
        textAlign: 'left',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        wordBreak: 'initial',
        whiteSpace: 'nowrap',
      },
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setSearchText('');
    }
  }, [open]);

  const renderTablePreview = () => (
    <Grid size={12}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 2,
          pb: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          <SearchTextField
            value={searchText}
            onChange={handleSearchChange}
            placeholder="Search"
            aria-label="Search"
            width="280px"
          />
          <Typography variant="body2" color="text.secondary">
            {filteredTableData.length} record
            {filteredTableData.length === 1 ? '' : 's'}
          </Typography>
        </Box>

        <Box
          sx={{
            width: '100%',
          }}
        >
          {filteredTableData.length > 0 ? (
            <ReactMuiTableVirtualizedV8
              data={filteredTableData}
              columns={columns}
              getColumnProps={getPreviewColumnProps}
              enableRowSelection={false}
              enablePagination={true}
              showToolbar={false}
              stickyHeader
              maxTableHeight={550}
              getCellProps={getPreviewCellProps}
            />
          ) : (
            <Box
              display="flex"
              minHeight={220}
              alignItems="center"
              justifyContent="center"
            >
              <NoDataPresentComponent
                message={
                  searchText?.trim() ? 'No result found.' : 'No data present.'
                }
              />
            </Box>
          )}
        </Box>
      </Box>
    </Grid>
  );

  const renderChartPreview = () => (
    <Grid size={12} justifyContent="center" alignItems="center">
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <Box
          component="img"
          src={getChartSource(previewItem?.item)}
          alt={previewItem?.title || 'Chart preview'}
          sx={{
            maxWidth: '100%',
            maxHeight: 500,
            objectFit: 'contain',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
          }}
        />
      </Box>
    </Grid>
  );

  return (
    <Dialog
      maxWidth="lg"
      fullWidth
      open={open}
      onClose={handleClose}
      slots={{
        transition: DialogTransition,
      }}
    >
      <DialogTitle>
        <Box component="span" aria-live="polite" aria-atomic="true">
          {previewItem?.title}
        </Box>
      </DialogTitle>

      <IconButton
        color="primary"
        aria-label="Close dialog"
        onClick={handleClosePreview}
        sx={{
          position: 'absolute',
          right: 16,
          top: 8,
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent dividers sx={{ paddingX: 3, paddingY: 2 }}>
        <Grid container size={12}>
          {isTablePreview ? renderTablePreview() : renderChartPreview()}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

PreviewTableChart.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  previewItem: PropTypes.shape({
    item: PropTypes.oneOfType([
      PropTypes.shape({
        columns: PropTypes.arrayOf(PropTypes.string),
        data: PropTypes.arrayOf(PropTypes.shape({})),
      }),
      PropTypes.string,
    ]),
    type: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
};

export default PreviewTableChart;
